// src/components/attendance/AttendanceRecordDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  Users, 
  BookOpen,
  School,
  Briefcase,
  ArrowLeft,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  BarChart3
} from 'lucide-react';
import { AttendanceService } from '../../services/attendanceService';
import { SessionService } from '../../services/sessionService';

const AttendanceRecordDetail = () => {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [attendanceRecord, setAttendanceRecord] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (recordId) {
      loadAttendanceRecord();
    }
  }, [recordId]);

  const loadAttendanceRecord = async () => {
    try {
      setLoading(true);
      const record = await AttendanceService.getAttendanceRecord(recordId);
      setAttendanceRecord(record);
      
      // Load session details
      if (record.sessionId) {
        const sessionData = await SessionService.getSession(record.sessionId);
        setSession(sessionData);
      }
    } catch (err) {
      setError('Failed to load attendance record');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceIcon = (status) => {
    switch (status) {
      case 'Present':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Late':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'Absent':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'Excused':
        return <CheckCircle className="w-4 h-4 text-blue-500" />;
      default:
        return <User className="w-4 h-4 text-gray-400" />;
    }
  };

  const getEngagementLabel = (level) => {
    switch (level) {
      case 'H': return 'High';
      case 'M': return 'Medium';
      case 'L': return 'Low';
      default: return 'Not recorded';
    }
  };

  const getBehaviourLabel = (level) => {
    switch (level) {
      case 'E': return 'Excellent';
      case 'G': return 'Good';
      case 'S': return 'Satisfactory';
      case 'N': return 'Needs Improvement';
      default: return 'Not recorded';
    }
  };

  const getCollaborationLabel = (level) => {
    switch (level) {
      case 'A': return 'Active';
      case 'P': return 'Participates';
      case 'R': return 'Reluctant';
      case 'I': return 'Independent';
      default: return 'Not recorded';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading attendance record...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !attendanceRecord) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="text-lg font-medium text-gray-900 mb-2">
              {error || 'Attendance Record Not Found'}
            </div>
            <button
              onClick={() => navigate('/sessions')}
              className="text-blue-600 hover:text-blue-800"
            >
              Return to Sessions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/sessions/${attendanceRecord.sessionId}`)}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Attendance Record</h1>
            <p className="text-gray-600">
              Recorded on {new Date(attendanceRecord.recordedAt).toLocaleDateString()} by {attendanceRecord.recordedBy}
            </p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>

        {/* Session Info */}
        {session && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Session Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                {session.sessionContext?.type === 'institution' ? (
                  <School className="w-4 h-4 text-blue-500" />
                ) : (
                  <Briefcase className="w-4 h-4 text-green-500" />
                )}
                <div>
                  <div className="text-sm text-gray-500">Context</div>
                  <div className="font-medium">{session.sessionContext?.name}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Session Date</div>
                  <div className="font-medium">
                    {new Date(session.sessionDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Subject</div>
                  <div className="font-medium">{session.subject}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Grades</div>
                  <div className="font-medium">{session.grades.join(', ')}</div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-sm text-gray-500">Session Title</div>
              <div className="font-medium">{session.sessionTitle}</div>
            </div>
          </div>
        )}

        {/* Attendance Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900 mb-1">{attendanceRecord.stats?.total}</div>
            <div className="text-sm text-gray-500">Total Learners</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">{attendanceRecord.stats?.present}</div>
            <div className="text-sm text-gray-500">Present</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600 mb-1">{attendanceRecord.stats?.late}</div>
            <div className="text-sm text-gray-500">Late</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-red-600 mb-1">{attendanceRecord.stats?.absent}</div>
            <div className="text-sm text-gray-500">Absent</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">{attendanceRecord.stats?.attendanceRate}%</div>
            <div className="text-sm text-gray-500">Attendance Rate</div>
          </div>
        </div>

        {/* Detailed Attendance */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Detailed Attendance Record
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Learner
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Attendance
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Engagement
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Behaviour
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Collaboration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attendanceRecord.attendanceData?.map((learner, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {learner.learnerSurname}, {learner.learnerName}
                          </div>
                          <div className="text-sm text-gray-500">
                            Grade {learner.grade} {learner.school && `• ${learner.school}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        {getAttendanceIcon(learner.attendance)}
                        <span className={`text-sm font-medium ${
                          learner.attendance === 'Present' ? 'text-green-600' :
                          learner.attendance === 'Late' ? 'text-yellow-600' :
                          learner.attendance === 'Absent' ? 'text-red-600' :
                          'text-blue-600'
                        }`}>
                          {learner.attendance}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        learner.engagement === 'H' ? 'bg-green-100 text-green-800' :
                        learner.engagement === 'M' ? 'bg-yellow-100 text-yellow-800' :
                        learner.engagement === 'L' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getEngagementLabel(learner.engagement)}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        learner.behaviour === 'E' ? 'bg-green-100 text-green-800' :
                        learner.behaviour === 'G' ? 'bg-blue-100 text-blue-800' :
                        learner.behaviour === 'S' ? 'bg-yellow-100 text-yellow-800' :
                        learner.behaviour === 'N' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getBehaviourLabel(learner.behaviour)}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        learner.collaboration === 'A' ? 'bg-green-100 text-green-800' :
                        learner.collaboration === 'P' ? 'bg-blue-100 text-blue-800' :
                        learner.collaboration === 'R' ? 'bg-yellow-100 text-yellow-800' :
                        learner.collaboration === 'I' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getCollaborationLabel(learner.collaboration)}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {learner.notes || '-'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceRecordDetail;